import { Typography, Modal, Space, Steps, Alert, Button, Skeleton } from 'antd'
import './CreateProposalModal.css'
import {
  ProposalCreationParameters,
  reducer,
  setTermsAndConditions,
  Action,
  ActionType,
  initialize,
  defaultState,
  preparePayloadForSave
} from './proposalsModel'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import { createContext, useEffect, useReducer, useState } from 'react'
import { Action as LineItemAction } from './lineItemsModel'
import InfoLinks from './InfoLinks'
import { faLeft, faRight, faSave } from '@fortawesome/pro-solid-svg-icons'
import IconAndTextButton from '../IconAndTextButton'
import { useCreateProposalModel, useCreateProposalModelInvalidator, useSaveProposalMutation } from './proposalQueries'
import Workareas from './Workareas'
import PaymentOptions from './PaymentOptions/PaymentOptions'

const ProposalSteps = {
  Workareas: 0,
  Information: 1,
  Payment: 2,
  WorkareaTable: 3
}

type ContextValue = { dispatch: (action: Action) => void }
export const ProposalContext = createContext<ContextValue>({ dispatch: () => { } })

export interface Props {
  jobId: number
  open: boolean
  onSave?: () => void
  onCancel?: (model: ProposalCreationParameters) => void
  onLinkCustomerToQbo?: () => void
  onLinkCostCodeToQbo?: (costCodeId: number) => void
  hideDownPayment?: boolean
}


export default function CreateProposalModal({
  jobId,
  open: visible,
  onSave = (() => { }),
  onCancel = (() => { }),
  onLinkCustomerToQbo = (() => { }),
  onLinkCostCodeToQbo = (() => { }),
  hideDownPayment = false,
}: Props) {
  const proposalQuery = useCreateProposalModel(jobId, visible)
  const proposalInvalidator = useCreateProposalModelInvalidator();

  const [isInitialized, setIsInitialized] = useState(false)
  const [model, dispatch] = useReducer(reducer, defaultState())

  useEffect(() => {
    if (!isInitialized && proposalQuery.data) {
      dispatch({ type: ActionType.DataLoaded, value: initialize(proposalQuery.data)})
      setIsInitialized(true)
    }
  }, [proposalQuery.data])

  useEffect(() => {
    setIsInitialized(false)
    proposalInvalidator.invalidate(jobId)
  }, [visible])

  const dispatchLineItem = (id: number) => (lineItemAction: LineItemAction) => dispatch({ type: ActionType.LineItemChanged, id, lineItemAction });

  const canSubmit = model.lineItems.some(x => x.enabled)

  const [currentStep, setCurrentStep] = useState(ProposalSteps.Workareas)
  useEffect(() => setCurrentStep(ProposalSteps.Workareas), [visible])

  const saveProposalMutation = useSaveProposalMutation()

  const saveAndNotify = () => {
    const payload = preparePayloadForSave(model)
    saveProposalMutation.mutate(payload, {
      onSuccess: () => {
        onSave()
        dispatch(setTermsAndConditions(proposalQuery.data?.termsAndConditions ?? ''))
      },
      onError: () => {
        alert("There was an error saving the proposal. Please validate all inputs.")
      }
    })
  }

  const footerButtons = []
  if (proposalQuery.isError) {
    footerButtons.push(<Button key="ok" type="primary" onClick={() => onCancel(model)}>OK</Button>)
  } else if (currentStep === ProposalSteps.Workareas) {
    footerButtons.push(<IconAndTextButton
      key="cancel"
      onClick={() => onCancel(model)}
      icon={faLeft}
      text="Cancel"
    />)

    footerButtons.push(<IconAndTextButton
      key="next"
      onClick={() => setCurrentStep(ProposalSteps.Information)}
      icon={faRight}
      text="Next"
      type="primary"
    />)
  } else if (currentStep === ProposalSteps.Information) {
    footerButtons.push(<IconAndTextButton
      key="back"
      onClick={() => setCurrentStep(ProposalSteps.Workareas)}
      icon={faLeft}
      text="Back"
    />)

    footerButtons.push(<IconAndTextButton
      key="next"
      onClick={() => setCurrentStep(ProposalSteps.Payment)}
      icon={faRight}
      text="Next"
      type="primary"
    />)
  } else {
    footerButtons.push(<IconAndTextButton
      key="back"
      onClick={() => setCurrentStep(ProposalSteps.Information)}
      icon={faLeft}
      text="Back"
    />)

    footerButtons.push(<IconAndTextButton
      type="primary"
      key="print"
      onClick={() => saveAndNotify()}
      disabled={!canSubmit}
      icon={faSave}
      text="Save"
      loading={saveProposalMutation.isLoading}
    />)

  }

  const handleLinkCustomerToQbo = (shouldSave: boolean) => {
    if (shouldSave) {
      saveProposalMutation.mutate(model, {
        onSuccess: () => {
          onLinkCustomerToQbo()
        },
        onError: () => {
          alert("There was an error saving the proposal. Please validate all inputs.")
        }
      })
    } else {
      onLinkCustomerToQbo()
    }
  }

  const handleLinkCostCodeToQbo = (costCodeId: number, shouldSave: boolean) => {
    if (shouldSave) {
      saveProposalMutation.mutate(model, {
        onSuccess: () => {
          onLinkCostCodeToQbo(costCodeId)
        },
        onError: () => {
          alert("There was an error saving the proposal. Please validate all inputs.")
        }
      })
    } else {
      onLinkCostCodeToQbo(costCodeId)
    }
  }

  return (
    <ProposalContext.Provider value={{ dispatch }}>
      <Modal title="Create Customer Proposal"
        open={visible}
        width={900}
        onCancel={() => onCancel(model)}
        footer={footerButtons}
      >
        {proposalQuery.isLoading && <Skeleton active />}
        {proposalQuery.isError &&
          <Alert
            type="error"
            showIcon
            message="Error loading data"
            description={(proposalQuery.error as Error).message as string}
          />
        }
        {proposalQuery.isSuccess && <>
          <Space direction='vertical' size="large" style={{ width: '100%' }}>
            <Steps
              current={currentStep}
              onChange={setCurrentStep}
              items={[
                { title: 'Workareas' },
                { title: 'Information' },
                { title: 'Payment' },
              ]} />

            {currentStep === ProposalSteps.Workareas &&
              <Workareas model={model} dispatch={dispatch} dispatchLineItem={dispatchLineItem} />
            }

            {currentStep === ProposalSteps.Information && <>
              <section>
                <Typography.Title level={5} style={{ margin: 0 }}>Terms & Conditions</Typography.Title>
                <RichTextEditor
                  value={model.termsAndConditions}
                  onChange={(e) => dispatch(setTermsAndConditions(e))}
                  data-testid='termsAndConditions'
                />
              </section>
              <section>
                <Typography.Title level={5} style={{ margin: 0 }}>Info</Typography.Title>
                <InfoLinks links={model.infoLinks} dispatch={dispatch} />
              </section>
            </>}

            {currentStep === ProposalSteps.Payment && <PaymentOptions
              model={model}
              hideDownPayment={hideDownPayment}
              dispatch={dispatch}
              onLinkCustomerToQbo={handleLinkCustomerToQbo}
              onLinkCostCodeToQbo={handleLinkCostCodeToQbo}
            />}
          </Space>
        </>}
      </Modal>
    </ProposalContext.Provider>
  )
}
